<template>
  <div
    :class="`hero ${isSmallHero ? 'hero-small' : ''} ${
      noSearchBar ? 'no-search-bar' : ''
    } ${(heroLinkText && heroLinkUrl) || (heroLinkText && heroLinkType === 'contact') ? 'has-button' : ''}`"
    :style="backgroundStyle"
  >
    <!-- Page title and icon -->
    <div class="hero__title">
      <i v-if="!!iconClass" :class="iconClass"></i>

      <template v-if="reverseTitles">
        <h1 class="hero__heading" v-html="seoTitle"></h1>
        <p class="hero__seo-heading" v-html="title"></p>
      </template>
      <template v-else>
        <h1 class="hero__heading" v-html="title"></h1>
        <p class="hero__seo-heading" v-html="seoTitle" v-if="seoTitle"></p>
        <span v-if="subTitle">{{ subTitle }}</span>
      </template>
    </div>

    <div class="hero__button" v-if="heroLinkText && heroLinkUrl">
      <a
        v-if="getHeroLinkIdIfThereIsOne(heroLinkUrl)"
        class="form-button primary"
        @click.prevent="scrollTo(getHeroLinkIdIfThereIsOne(heroLinkUrl))"
      >
        <span>{{ heroLinkText }}</span>
      </a>
      <form-button
        v-else-if="heroLinkType === 'contact'"
        :text="heroLinkText"
        @click.native="openContactForm()"
        theme="primary"
      ></form-button>
      <form-button
        v-else-if="heroLinkType === 'evaluation-visit'"
        :text="heroLinkText"
        @click.native="openEvaluationForm()"
        theme="primary"
      ></form-button>
      <a
        v-else-if="heroLinkType === 'internal' && heroLinkNavigateToContactPage"
        :href="getHeroLinkHref(heroLinkUrl)"
        class="form-button primary"
        @click.prevent="navigateToContactPage(heroLinkUrl)"
      >
        <span>{{ heroLinkText }}</span>
      </a>
      <a :href="getHeroLinkHref(heroLinkUrl)" target="_blank" class="form-button primary" v-else>
        <span>{{ heroLinkText }}</span>
      </a>
    </div>

    <agency-search-box
      v-if="hasAgencySearchBar"
      action="navigate"
      :types="['agent', 'city', 'company']"
      class="hero-search"
    ></agency-search-box>

    <div v-if="!noSearchBar" class="hero__search">
      <!-- Hero tabs -->
      <div :class="`tabs ${selectedTab}`">
        <RouterLink class="tab" :id="config.testing.FRONT_SEARCH_TAB_SELLING" to="/" tabindex="0">
          <span class="mobile">Myy</span>
          <span class="desktop">Myymässä</span>
        </RouterLink>
        <RouterLink class="tab" :id="config.testing.FRONT_SEARCH_TAB_BUYING" to="/myytavat" tabindex="0">
          <span class="mobile">Osta</span>
          <span class="desktop">Ostamassa</span>
        </RouterLink>
        <RouterLink class="tab" :id="config.testing.FRONT_SEARCH_TAB_RENTING" to="/vuokraamassa" tabindex="0">
          <span class="mobile">Vuokraa</span>
          <span class="desktop">Vuokraamassa</span>
        </RouterLink>
      </div>

      <!-- Search boxes -->
      <apartments-search-box v-if="selectedTab === heroTabs.BUYING"></apartments-search-box>
      <agency-search-box
        v-if="selectedTab === heroTabs.SELLING"
        action="navigate"
        :types="['agent', 'city', 'company']"
        class="hero-search"
        :useLabel="true"
      ></agency-search-box>
      <apartments-search-box v-if="selectedTab === heroTabs.RENTING" mode="rent"></apartments-search-box>
    </div>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import ApartmentsSearchBox from '../apartments-search-box/apartments-search-box.vue';
import VueScrollTo from 'vue-scrollto';
import { defineAsyncComponent, defineComponent } from 'vue';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import { useHead } from '@unhead/vue';

export default defineComponent({
  name: 'hero',
  setup(props) {
    const route = useSafeRoute();

    if (props.imageUrl) {
      useHead({
        link: [
          {
            rel: 'preload',
            href: props.imageUrl + '?fm=webp',
            as: 'image',
          },
        ],
      });
    }

    return { route };
  },
  props: {
    imageUrl: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      required: false,
    },
    seoTitle: {
      type: String,
      required: false,
    },
    reverseTitles: {
      type: Boolean,
      required: false,
    },
    iconClass: {
      type: String,
      required: false,
    },
    heroLinkText: {
      type: String,
      required: false,
    },
    heroLinkUrl: {
      type: String,
      required: false,
    },
    heroLinkType: {
      type: String,
      required: false,
    },
    selectedTab: {
      type: String,
      required: false,
    },
    noSearchBar: {
      type: Boolean,
      required: false,
    },
    isSmallHero: {
      type: Boolean,
      required: false,
    },
    heroLinkNavigateToContactPage: {
      type: Boolean,
      required: false,
    },
    hasAgencySearchBar: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    config,
    heroTabs: {
      BUYING: 'buying',
      SELLING: 'selling',
      RENTING: 'renting',
    },
  }),
  computed: {
    backgroundStyle() {
      return this.imageUrl ? `background-image: url(${this.imageUrl}?fm=webp);` : '';
    },
  },
  methods: {
    getHeroLinkHref(link: string) {
      // Temporary hack to detect mailto links for now
      if (link.includes('mailto:')) {
        return link;
      }

      const id = getRouteParamAsString(this.route.params.id);
      if (id) {
        return `${link}/${id}`;
      }
      return link;
    },
    getHeroLinkIdIfThereIsOne(link: string) {
      return link.split('#').length > 1 ? link.split('#')[1] : null;
    },
    navigateToContactPage(link: string) {
      void this.$router.push(link);
    },
    scrollTo(id: string | null) {
      if (!id) {
        return;
      }
      const duration = 400;
      const options = {
        easing: 'ease-out',
        offset: -50,
        cancelable: true,
      };
      VueScrollTo.scrollTo(document.getElementById(id), duration, options);
    },
    openEvaluationForm() {
      this.$bus.$emit('openDialogue', 'contact-order-evaluation');
    },
    openContactForm() {
      this.$bus.$emit('openDialogue', 'contact-full');
    },
  },
  components: {
    AgencySearchBox: defineAsyncComponent(() => import('@/app/components/agency-search-box/agency-search-box.vue')),
    ApartmentsSearchBox,
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
  },
});
</script>
